import React from 'react';

/**
 * SwellingSection Component
 * Detailed swelling assessment (especially important for ankle)
 * Can be simple or detailed based on config
 */
const SwellingSection = ({ config, formData, onChange }) => {
  const handleFieldChange = (fieldId, value) => {
    onChange(fieldId, value);
  };

  const handleCheckboxChange = (fieldId, option) => {
    const currentValues = formData[fieldId] || [];
    const newValues = currentValues.includes(option)
      ? currentValues.filter(v => v !== option)
      : [...currentValues, option];
    onChange(fieldId, newValues);
  };

  const shouldShowField = (field) => {
    if (!field.conditional) return true;
    
    const { field: condField, value: condValue } = field.conditional;
    return formData[condField] === condValue;
  };

  const renderField = (field) => {
    if (!shouldShowField(field)) return null;

    const value = formData[field.id] || '';

    return (
      <div key={field.id} className="field-group">
        <label className="field-label">
          {field.label}
          {field.required && <span className="required">*</span>}
        </label>

        {field.type === 'text' && (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="form-input"
          />
        )}

        {field.type === 'textarea' && (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
            className="form-textarea"
          />
        )}

        {field.type === 'radio' && (
          <div className="radio-group">
            {field.options.map(option => (
              <label key={option} className="radio-option">
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'select' && (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            {field.options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        )}

        {field.type === 'checkbox-multiple' && (
          <div className="checkbox-group">
            {field.options.map(option => (
              <label key={option} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={(value || []).includes(option)}
                  onChange={() => handleCheckboxChange(field.id, option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {field.note && (
          <p className="field-note">{field.note}</p>
        )}
      </div>
    );
  };

  // Simple version (basic yes/no)
  if (config.simple) {
    return (
      <div className="swelling-section simple">
        <style jsx>{`
          .swelling-section {
            background: #f0f9ff;
            border: 2px solid #bae6fd;
            border-radius: 8px;
            padding: 24px;
            margin: 24px 0;
          }

          .field-group {
            background: white;
            padding: 16px;
            border-radius: 6px;
            margin-bottom: 16px;
            border: 1px solid #e0f2fe;
          }

          .field-label {
            display: block;
            font-weight: 600;
            color: #1f2937;
            margin-bottom: 10px;
            font-size: 15px;
          }

          .required {
            color: #dc2626;
            margin-left: 4px;
          }

          .radio-group {
            display: flex;
            gap: 24px;
          }

          .radio-option {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
            padding: 8px 12px;
            border-radius: 4px;
            transition: background 0.2s;
          }

          .radio-option:hover {
            background: #f9fafb;
          }

          .radio-option input {
            width: 18px;
            height: 18px;
            cursor: pointer;
          }

          .form-textarea {
            width: 100%;
            padding: 10px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            font-family: inherit;
            resize: vertical;
          }

          .form-textarea:focus {
            outline: none;
            border-color: #0ea5e9;
            box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
          }
        `}</style>

        <div className="field-group">
          <label className="field-label">
            Swelling present?
          </label>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="swellingPresent"
                value="Yes"
                checked={formData.swellingPresent === 'Yes'}
                onChange={(e) => handleFieldChange('swellingPresent', e.target.value)}
              />
              <span>Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="swellingPresent"
                value="No"
                checked={formData.swellingPresent === 'No'}
                onChange={(e) => handleFieldChange('swellingPresent', e.target.value)}
              />
              <span>No</span>
            </label>
          </div>
        </div>

        {formData.swellingPresent === 'Yes' && (
          <div className="field-group">
            <label className="field-label">
              Describe the swelling:
            </label>
            <textarea
              value={formData.swellingDescription || ''}
              onChange={(e) => handleFieldChange('swellingDescription', e.target.value)}
              placeholder="Location, size, when it appears, etc."
              rows={3}
              className="form-textarea"
            />
          </div>
        )}
      </div>
    );
  }

  // Detailed version (for ankle)
  return (
    <div className="swelling-section detailed">
      <style jsx>{`
        .swelling-section {
          background: #f0f9ff;
          border: 2px solid #0ea5e9;
          border-radius: 8px;
          padding: 24px;
          margin: 24px 0;
        }

        .section-header {
          background: #e0f2fe;
          border-left: 4px solid #0ea5e9;
          padding: 14px;
          border-radius: 6px;
          margin-bottom: 20px;
        }

        .section-header h4 {
          color: #075985;
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 6px 0;
        }

        .section-header p {
          color: #0c4a6e;
          font-size: 13px;
          margin: 0;
        }

        .field-group {
          background: white;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 16px;
          border: 1px solid #e0f2fe;
        }

        .field-label {
          display: block;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 10px;
          font-size: 15px;
        }

        .required {
          color: #dc2626;
          margin-left: 4px;
        }

        .form-input,
        .form-textarea,
        .form-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .form-input:focus,
        .form-textarea:focus,
        .form-select:focus {
          outline: none;
          border-color: #0ea5e9;
          box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
        }

        .form-textarea {
          resize: vertical;
        }

        .radio-group {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .radio-option:hover {
          background: #f9fafb;
        }

        .radio-option input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .checkbox-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .checkbox-option:hover {
          background: #f9fafb;
        }

        .checkbox-option input {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .field-note {
          margin-top: 8px;
          font-size: 13px;
          color: #0c4a6e;
          font-style: italic;
        }

        @media (max-width: 640px) {
          .swelling-section {
            padding: 16px;
          }

          .radio-group {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>

      <div className="section-header">
        <h4>💧 Detailed Swelling Assessment</h4>
        <p>Important for tracking progression and treatment response</p>
      </div>

      <div className="fields-container">
        {config.fields && config.fields.map(field => renderField(field))}
      </div>
    </div>
  );
};

export default SwellingSection;
